import React, {KeyboardEvent, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {pageSizeChoices} from "../paginationPreferences";

export const pageSizeOptions = pageSizeChoices;

export type PaginationState = {
  currentPage: number;
  totalPages: number;
  count: number;
};

type Props = PaginationState & {
  pageSize: number;
  disabled?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

const PAGE_WINDOW = 5;
const arrowStroke = {fill: "none", stroke: "currentColor", strokeWidth: 2.1, strokeLinecap: "round", strokeLinejoin: "round"} as const;

function PaginationArrowIcon({direction, double = false}: {direction: "left" | "right"; double?: boolean}) {
  const first = direction === "left" ? "M14.5 6.5L9 12l5.5 5.5" : "M9.5 6.5L15 12l-5.5 5.5";
  const second = direction === "left" ? "M19 6.5L13.5 12l5.5 5.5" : "M5 6.5l5.5 5.5L5 17.5";
  return <svg className={double ? "is-double" : ""} viewBox="0 0 24 24" aria-hidden="true"><path d={first} {...arrowStroke}/>{double && <path d={second} {...arrowStroke}/>}</svg>;
}

const clampPage = (page: number, totalPages: number) => Math.min(Math.max(1, page), Math.max(1, totalPages));

export default function Pagination({currentPage, totalPages, count, pageSize, disabled = false, onPageChange, onPageSizeChange}: Props) {
  const {t} = useTranslation();
  const [draftPage, setDraftPage] = useState(String(currentPage));
  const safeTotalPages = Math.max(1, Number(totalPages || 1));
  const safeCurrentPage = clampPage(Number(currentPage || 1), safeTotalPages);

  useEffect(() => { setDraftPage(String(safeCurrentPage)); }, [safeCurrentPage]);

  const pages = useMemo(() => {
    const maxStart = Math.max(1, safeTotalPages - PAGE_WINDOW + 1);
    const blockStart = Math.min(Math.max(1, safeCurrentPage - Math.floor(PAGE_WINDOW / 2)), maxStart);
    const blockEnd = Math.min(blockStart + PAGE_WINDOW - 1, safeTotalPages);
    return Array.from({length: blockEnd - blockStart + 1}, (_, index) => blockEnd - index);
  }, [safeCurrentPage, safeTotalPages]);

  const currentBlockStart = pages.length ? Math.min(...pages) : 1;
  const currentBlockEnd = pages.length ? Math.max(...pages) : 1;
  const previousBlockPage = Math.max(1, safeCurrentPage - PAGE_WINDOW);
  const nextBlockPage = Math.min(safeTotalPages, safeCurrentPage + PAGE_WINDOW);
  const commitDraftPage = () => {
    const nextPage = clampPage(Number(draftPage), safeTotalPages);
    setDraftPage(String(nextPage));
    if (nextPage !== safeCurrentPage) onPageChange(nextPage);
  };
  const onDraftKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") commitDraftPage();
    if (event.key === "Escape") setDraftPage(String(safeCurrentPage));
  };

  return <div className="pagination" aria-label={t("pagination.label", {defaultValue: "Pagination"})}>
    <div className="pagination-summary">
      <span>{t("page")} <b>{safeCurrentPage}</b> {t("of")} {safeTotalPages} · {count} {t("totalRecords")}</span>
      <label>{t("pagination.perPage", {defaultValue: "Rows per page"})}
        <select value={pageSize} disabled={disabled} onChange={event => onPageSizeChange(Number(event.target.value))}>
          {pageSizeOptions.map(option => <option key={option} value={option}>{option}</option>)}
        </select>
      </label>
    </div>
    <div className="pagination-controls">
      <button type="button" className="secondary pagination-arrow" title={t("pagination.previousBlock", {defaultValue: "Previous pages"})} disabled={disabled || currentBlockStart <= 1} onClick={() => onPageChange(previousBlockPage)}><PaginationArrowIcon direction="left" double/></button>
      <button type="button" className="secondary pagination-arrow" title={t("previous")} disabled={disabled || safeCurrentPage <= 1} onClick={() => onPageChange(safeCurrentPage - 1)}><PaginationArrowIcon direction="left"/></button>
      <div className="pagination-pages">
        {pages.map(page => page === safeCurrentPage ?
          <input key={page} className="pagination-page-input" type="number" min={1} max={safeTotalPages} value={draftPage} disabled={disabled} title={t("pagination.currentPage", {defaultValue: "Current page"})} aria-label={t("pagination.currentPage", {defaultValue: "Current page"})} onChange={event => setDraftPage(event.target.value)} onBlur={commitDraftPage} onKeyDown={onDraftKeyDown}/> :
          <button key={page} type="button" className="secondary pagination-page" title={`${t("page")} ${page}`} disabled={disabled} onClick={() => onPageChange(page)}>{page}</button>
        )}
      </div>
      <button type="button" className="secondary pagination-arrow" title={t("next")} disabled={disabled || safeCurrentPage >= safeTotalPages} onClick={() => onPageChange(safeCurrentPage + 1)}><PaginationArrowIcon direction="right"/></button>
      <button type="button" className="secondary pagination-arrow" title={t("pagination.nextBlock", {defaultValue: "Next pages"})} disabled={disabled || currentBlockEnd >= safeTotalPages} onClick={() => onPageChange(nextBlockPage)}><PaginationArrowIcon direction="right" double/></button>
    </div>
  </div>;
}
